import React from 'react';
import { OneWayInOpportunitySimpleV2Dto } from 'src/api/v2';
import { useAdvisorLookup } from 'src/features/users';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, FaIcon, Spinner } from '@innovationnorway/cl-react';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { Dropdown } from '@innovationnorway/cl-react';
import useAdvisorOptions from 'src/features/siva/ideas-and-companies/hooks/use-advisor-options';
import { ROUTES } from 'src/navigation';
import { useNavigate } from 'react-router-dom';
import useHandleAddAdvisor from '../../hooks/use-handle-add-advisor';
import useUser from 'src/features/authorization/hooks/use-user';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';
import AdvisorButton from './AdvisorButton';

/**
 * The interactive half of AdvisorDropdown. Mounted only once the user opens the dropdown, because
 * its hooks, popover and dialog are far too expensive to carry on every card of the board.
 */
const AdvisorDropdownPanel = ({
  opportunity,
  dropdownId,
}: {
  opportunity: Pick<OneWayInOpportunitySimpleV2Dto, 'id' | 'oneWayInId' | 'assignedTo' | 'clusterId'>;
  dropdownId: string;
}) => {
  const { advisorNameById, advisorInitialsById, advisorColorClassById } = useAdvisorLookup();
  const { user } = useUser();
  const { advisorOptions } = useAdvisorOptions();

  const siteimprove = useSiteimprove();
  const navigate = useNavigate();
  // Mounting this panel *is* the first open, so the popover starts visible.
  const [open, setOpen] = React.useState(true);
  const [dialogIsOpen, setDialogIsOpen] = React.useState(false);
  const { t: tCommon } = useTranslation('common');
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { handleAddAdvisor, isLoading: assigningAdvisor } = useHandleAddAdvisor(opportunity);

  const [newAdvisorId, setNewAdvisorId] = React.useState<string | null>(null);
  const [newAdvisorName, setNewAdvisorName] = React.useState<string | null>(null);

  const assignedTo = opportunity?.assignedTo;
  const currentAdvisorName = assignedTo ? advisorNameById.get(assignedTo) : undefined;
  const currentAdvisorInitials = assignedTo ? advisorInitialsById.get(assignedTo) : undefined;
  const advisorColorClass = (assignedTo && advisorColorClassById.get(assignedTo)) || '';

  const electiveAdvisors = React.useMemo(
    () => advisorOptions.filter((option) => option.value !== assignedTo),
    [advisorOptions, assignedTo],
  );

  const logOpen = React.useCallback(() => {
    if (location.pathname === ROUTES.ONE_WAY_IN.BASE) {
      siteimprove.log(
        SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.id,
        SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.actions.OPEN_ASSIGN_ADVISOR_MODAL,
      );
    } else if (
      location.pathname.replace(/%7C/g, '|').includes(ROUTES.ONE_WAY_IN.DETAILS.BASE(opportunity.oneWayInId))
    ) {
      siteimprove.log(
        SITEIMPROVE_DECLARATIONS.OPPORTUNITY_DETAILS.id,
        SITEIMPROVE_DECLARATIONS.OPPORTUNITY_DETAILS.actions.OPEN_ASSIGN_ADVISOR_DROPDOWN,
      );
    }
  }, [opportunity.oneWayInId, siteimprove]);

  const hasLoggedInitialOpen = React.useRef(false);
  React.useEffect(() => {
    if (hasLoggedInitialOpen.current) return;
    hasLoggedInitialOpen.current = true;
    logOpen();
  }, [logOpen]);

  const onAssignAdvisor = async (advisorId: string, advisorName: string): Promise<void> => {
    setNewAdvisorId(advisorId);
    setNewAdvisorName(advisorName);
    if (user?.clusterUserId === advisorId) {
      await handleAddAdvisor(advisorId, false);
    } else {
      setDialogIsOpen(true);
    }
  };

  const handleShouldSendEmail = async (shouldSendEmail: boolean) => {
    await handleAddAdvisor(newAdvisorId!, shouldSendEmail);
    setDialogIsOpen(false);
  };

  return (
    <>
      <AdvisorButton
        dropdownId={dropdownId}
        initials={currentAdvisorInitials}
        colorClass={advisorColorClass}
        label={currentAdvisorName ? tOneWayIn('advisor.changeAdvisor') : tOneWayIn('advisor.assignAdvisor')}
      />

      <Dropdown
        id={dropdownId}
        data-size="sm"
        open={open}
        onOpen={() => {
          setOpen(true);
          logOpen();
        }}
        onClose={() => setOpen(false)}
      >
        {currentAdvisorName ? (
          <>
            <Dropdown.Heading>{tOneWayIn('advisor.currentlyAssigned')}</Dropdown.Heading>
            <Dropdown.List>
              <Dropdown.Item>
                <Dropdown.Button className="pointer-events--none">{currentAdvisorName}</Dropdown.Button>
              </Dropdown.Item>
            </Dropdown.List>
            <Dropdown.Heading>{tOneWayIn('advisor.changeAdvisor')}</Dropdown.Heading>
          </>
        ) : (
          <Dropdown.Heading>{tOneWayIn('advisor.assignAdvisor')}</Dropdown.Heading>
        )}
        <Dropdown.List>
          {electiveAdvisors.map((option) => (
            <Dropdown.Item key={option.value}>
              <Dropdown.Button onClick={() => onAssignAdvisor(option.value, option.fullName)}>
                {option.fullName}
              </Dropdown.Button>
            </Dropdown.Item>
          ))}
          <Dropdown.Item>
            <Dropdown.Button onClick={() => navigate(ROUTES.ACTOR_PROFILE.SETTINGS.ACCESS_CONTROL)}>
              <FaIcon icon={icon({ prefix: 'fal', iconName: 'user-plus' })} />
              {tOneWayIn('tooltip.addAdvisor')}
            </Dropdown.Button>
          </Dropdown.Item>
        </Dropdown.List>
      </Dropdown>
      <Dialog open={dialogIsOpen} onClose={() => handleShouldSendEmail(false)} closedby="any">
        {dialogIsOpen && (
          <>
            <span className="dialog-with-sidebar--heading">
              {tOneWayIn('opportunity.addAdvisor.dialog.sendEmailQuestion', {
                advisorName: newAdvisorName ?? tOneWayIn('opportunity.addAdvisor.dialog.advisorFallback'),
              })}
            </span>
            <div className="margin-top--ds-4 display--flex gap--2">
              <Button onClick={() => handleShouldSendEmail(true)}>{tCommon('yes')}</Button>
              <Button variant="secondary" onClick={() => handleShouldSendEmail(false)}>
                {tCommon('no')}
              </Button>
              {assigningAdvisor && <Spinner aria-label="Tilordner rådgiver" />}
            </div>
          </>
        )}
      </Dialog>
    </>
  );
};

export default AdvisorDropdownPanel;
